import { SortOption } from './_models/sort-option.model';
import { BaseModel } from '../../models/base/base.model';
import { FilterOptions } from './_models/filter-options.model';
import { UniqueMode } from './_enums/unique-mode.enum';
import { Unique } from './_enums/unique.enum';
import { Sort } from './_enums/sort.enum';
import { FilterScope } from './_enums/filter-scope.enum';
import { FilterCase } from './_enums/filter-case.enum';
import { RegexUtil } from '../regex/regex.util';
import * as _ from 'lodash';

/**
 * A utility for sorting and filtering arrays
 */
export class CollectionUtil {
  // ********************
  // Public Static methods
  // ********************

  /**
   * Append an item to an array
   * @param collection - The array of items
   * @param item - The item to append to the array
   * @param unique - Enum indicating whether uniqueness is enforced
   * @param uniqueMode - Enum indicating how uniqueness will be determined
   * @returns True if the item was successfully appended
   */
  public static append<T>(
    collection: T[],
    item: T,
    unique: Unique = Unique.True,
    uniqueMode: UniqueMode = UniqueMode.Id
  ): boolean {
    if (unique === Unique.True) {
      if (
        uniqueMode === UniqueMode.Id &&
        item.hasOwnProperty('_id') &&
        CollectionUtil.isUniqueById(collection, item) === false
      ) {
        return false;
      } else if (
        uniqueMode === UniqueMode.Object &&
        CollectionUtil.isUniqueByObject(collection, item) === false
      ) {
        return false;
      }
    }
    collection.push(item);
    return true;
  }

  /**
   * Fitler an array of objects
   * @param collection - The array of objects to filter
   * @param filter - The filter string
   * @param filterColumns - The object properties to use when filtering
   * @param filterOptions - The filter options
   * @returns An array of filtered objects
   */
  public static filter<T>(
    collection: T[],
    filter: string,
    filterColumns: string[],
    filterOptions?: FilterOptions
  ): T[] {
    // If the filter string or collection are not valid then just return the collection unchanged
    if (!filter || !collection) {
      return collection;
    }
    if (!filterOptions) {
      filterOptions = new FilterOptions();
    }
    // Filter the collection using a lambda expression
    const filteredData = collection.filter(o => {
      // For each of the filter columns
      for (const filterColumn of filterColumns) {
        // Get the text of the current column
        const text: string = o[filterColumn];
        // Create a regular expression for the passed filter string and filter options
        const regExp = this.getFilterRegex(filter, filterOptions);
        // If the contents of the current column matches the filter string then
        if (text.search(regExp) >= 0) {
          // Return true (collection row is included in the filtered data)
          return true;
        }
      }
      // Return false (collection row is not included in the filtered data)
      return false;
    });
    // Return the filtered data
    return filteredData;
  }

  /**
   * Find an object in an array of objects by matching an object property
   * @param collection - The array of objects to search
   * @param property - The object property to search
   * @param value - The value to match
   * @returns The matching object
   */
  public static find<T>(collection: T[], property: string, value: any): T {
    const object = collection.find(o => o[property] === value);
    return object;
  }

  /**
   * Find an object in an array of objects by matching the object's ID
   * @param collection - The array of objects to search
   * @param id - The object ID to search for
   * @returns The matching object
   */
  public static findById<T extends BaseModel>(collection: T[], id: string): T {
    const object = collection.find(o => o._id === id);
    return object;
  }

  /**
   * Get the index of an object in an array of objects
   * @param collection - The array of objects to search
   * @param item - The item to search for
   * @returns The index of the item in the array (zero-based)
   */
  public static findIndex<T>(collection: T[], item: T): number {
    const index = collection.findIndex(o => o === item);
    return index;
  }

  /**
   * Insert an object into an array of objects
   * @param collection - The array of objects into which the object will be inserted
   * @param item - The item to insert into the array
   * @param index - The desired location of the item in the array
   * @returns True if the insert succeeded
   */
  public static insert<T>(
    collection: T[],
    item: T,
    index: number = 0
  ): boolean {
    collection.splice(index, 0, item);
    return true;
  }

  /**
   * Sort an array of objects by mutliple keys. The method sorts the passed array in-place as opposed to
   * returning the sorted array.
   * @param collection - The array of objects to sort
   * @param sortOptions - The sort options
   */
  public static multiKeySort<T>(
    collection: T[],
    ...sortOptions: SortOption[]
  ): void {
    collection.sort((a: any, b: any) => {
      for (const sortOption of sortOptions) {
        if (typeof a[sortOption.key] === 'boolean') {
          if (!a[sortOption.key] && b[sortOption.key]) {
            return sortOption.order === Sort.Ascending ? -1 : 1;
          } else if (a[sortOption.key] && !b[sortOption.key]) {
            return sortOption.order === Sort.Ascending ? 1 : -1;
          }
        } else if (
          !RegexUtil.isIsoDate(a[sortOption.key]) &&
          typeof a[sortOption.key] === 'string'
        ) {
          if (
            a[sortOption.key].toLowerCase() < b[sortOption.key].toLowerCase()
          ) {
            return sortOption.order === Sort.Ascending ? -1 : 1;
          } else if (
            a[sortOption.key].toLowerCase() > b[sortOption.key].toLowerCase()
          ) {
            return sortOption.order === Sort.Ascending ? 1 : -1;
          }
        } else {
          if (a[sortOption.key] < b[sortOption.key]) {
            return sortOption.order === Sort.Ascending ? -1 : 1;
          } else if (a[sortOption.key] > b[sortOption.key]) {
            return sortOption.order === Sort.Ascending ? 1 : -1;
          }
        }
      }
      return 0;
    });
  }

  /**
   * Remove an object from an array of objects by matching the object ID
   * @param collection - The array of objects from which to remove an object
   * @param item - The item to remove from the array
   * @returns The array of objects with the item removed
   */
  public static remove<T extends BaseModel>(collection: T[], item: T): T[] {
    const updatedCollection = collection.filter(o => o._id !== item._id);
    return updatedCollection;
  }

  /**
   * Remove an object from an array of object by matching the passed object ID
   * @param collection - The array of objects from which to remove an object
   * @param ID - The ID of the item to remove from the array
   * @returns The array of objects with the item removed
   */
  public static removeById<T extends BaseModel>(
    collection: T[],
    id: string
  ): T[] {
    const updatedCollection = collection.filter(o => o._id !== id);
    return updatedCollection;
  }

  /**
   * Shuffly an array of objects
   * @param collection - The array of objects to shuffly
   * @returns The shuffled array of objects
   */
  public static shuffle<T>(collection: T[]): T[] {
    let currentIndex = collection.length;
    let temporaryValue: T;
    let randomIndex: number;

    // While there remain elements to shuffle...
    while (0 !== currentIndex) {
      // Pick a remaining element...
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex -= 1;

      // Swap it with the current element.
      temporaryValue = collection[currentIndex];
      collection[currentIndex] = collection[randomIndex];
      collection[randomIndex] = temporaryValue;
    }

    return collection;
  }

  /**
   * Sort an array of objects by a single property. The method sorts the passed array in-place as opposed to
   * returning the sorted array.
   * @param collection - The array of objects to sort
   * @param key - The property to use for sorting
   * @param order - The sort order
   */
  public static sort<T>(collection: T[], key: string, order: Sort): void {
    collection.sort((a: any, b: any) => {
      if (!RegexUtil.isIsoDate(a[key]) && typeof a[key] === 'string') {
        if (a[key].toLowerCase() < b[key].toLowerCase()) {
          return order === Sort.Ascending ? -1 : 1;
        } else if (a[key].toLowerCase() > b[key].toLowerCase()) {
          return order === Sort.Ascending ? 1 : -1;
        }
        return 0;
      } else {
        if (a[key] < b[key]) {
          return order === Sort.Ascending ? -1 : 1;
        } else if (a[key] > b[key]) {
          return order === Sort.Ascending ? 1 : -1;
        }
        return 0;
      }
    });
  }

  /**
   * Update an object in an array of objects. Use the object's ID to match objects.
   * @param collection - The array of objects that contains the object to update
   * @param item - The item to update
   * @param addIfMissing - Boolean indicating whether to add the item if it is not present in the array
   * @returns The array of objects with the updated item
   */
  public static update<T extends BaseModel>(
    collection: T[],
    item: T,
    addIfMissing: boolean = false
  ): T[] {
    const index = collection.findIndex(o => o._id === item._id);
    if (index < 0) {
      if (addIfMissing === true) {
        collection.push(item);
        return collection;
      } else {
        return collection;
      }
    }
    collection[index] = item;
    return collection;
  }

  // ********************
  // Private Static methods
  // ********************

  /**
   * Create a regular expression object that implements the passed filter options
   * @param filter - The filter text
   * @param filterOptions - The filter options
   * @returns A regular expression object
   */
  private static getFilterRegex(
    filter: string,
    filterOptions: FilterOptions
  ): RegExp {
    filter = this.escapeRegexCharacters(filter);
    let regexString: string;
    switch (filterOptions.fitlerScope) {
      case FilterScope.StartsWith:
        regexString = '^' + filter;
        break;
      case FilterScope.EndsWith:
        regexString = filter + '$';
        break;
      default:
        regexString = filter;
        break;
    }
    let regExp: RegExp;
    if (filterOptions.fitlerCase === FilterCase.CaseSensitive) {
      regExp = new RegExp('.*' + regexString + '.*');
    } else {
      regExp = new RegExp('.*' + regexString + '.*', 'i');
    }
    return regExp;
  }

  /**
   * Determine whether an object exists in an array of objects by comparing the object properties
   * @param collection - The array of objects to search
   * @param item - The item to search for
   * @returns True if the object is unique
   */
  private static isUniqueByObject<T>(collection: T[], item: T) {
    for (const collectionItem of collection) {
      if (_.isEqual(collectionItem, item)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Determine whether an object exists in an array of objects by comparing the object IDs
   * @param collection - The array of objects to search
   * @param item - The item to search for
   * @returns True if the object is unique
   */
  private static isUniqueById<T>(collection: any[], item: any) {
    const matchingItem = collection.find(o => o._id === item._id);
    if (matchingItem) {
      return false;
    }
    return true;
  }

  /**
   * Escape a regular expression string
   * @param regexString - The regex string to escape
   * @returns The escaped regular expression string
   */
  private static escapeRegexCharacters(regexString: string): string {
    const escapedText = regexString.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    return escapedText;
  }
}
